import React from "react";
import { Box, Chip, InputAdornment, Paper, TextField, Typography, makeStyles } from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import SearchIcon from "@material-ui/icons/Search";
import tokens from "./designTokens";

export const BAND_OPTIONS = [
  { value: "all", label: "All bands" },
  { value: "healthy", label: "Healthy" },
  { value: "watch", label: "Watch" },
  { value: "at-risk", label: "At risk" },
  { value: "watchlist", label: "New agents" },
];

const RANGE_CHIPS = [
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "custom", label: "Custom day" },
];

const useStyles = makeStyles({
  bar: {
    border: `1px solid ${tokens.border}`,
    borderRadius: 12,
    boxShadow: "0 1px 2px rgba(16,24,40,.04)",
    padding: "14px 20px 16px",
  },
  heading: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    fontFamily: tokens.fontHeading,
    fontWeight: 600,
    fontSize: 14,
    color: "#232D2F",
    marginBottom: 12,
  },
  fieldLabel: {
    display: "block",
    fontFamily: tokens.fontBody,
    fontSize: 11,
    fontWeight: 600,
    color: tokens.textSecondary,
    marginBottom: 4,
  },
  field: {
    "&.MuiTextField-root": {
      backgroundColor: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: 8,
    },
    "&.MuiTextField-root:focus-within": { borderColor: tokens.primary },
    "& .MuiInputBase-root": { padding: "0 8px", minHeight: 34 },
    "& .MuiInputBase-input": { fontSize: 12.5, fontFamily: tokens.fontBody, color: tokens.textPrimary },
  },
  rangeChip: {
    background: "#F7F8FA",
    color: tokens.textSecondary,
    border: `1px solid ${tokens.border}`,
    fontFamily: tokens.fontBody,
    fontSize: 12.5,
    fontWeight: 600,
  },
  rangeChipOn: {
    background: tokens.primaryLight,
    color: tokens.primary,
    border: "1px solid #B9D6D1",
    fontWeight: 700,
    "&:hover, &:focus": { background: tokens.primaryLight },
  },
  dateInput: {
    border: `1px solid ${tokens.border}`,
    borderRadius: 999,
    background: tokens.surface,
    color: tokens.textPrimary,
    padding: "5px 12px",
    fontFamily: tokens.fontBody,
    fontSize: 12.5,
    fontWeight: 600,
    outline: "none",
  },
});

export function RangeChips({ preset, onPreset, customDate, onCustomDate }) {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={8}>
      {RANGE_CHIPS.map((r) => {
        const on = preset === r.key;
        return (
          <Chip
            key={r.key}
            size="small"
            clickable
            data-testid={`range-chip-${r.key}`}
            label={r.label}
            onClick={() => onPreset(r.key)}
            className={`${classes.rangeChip} ${on ? classes.rangeChipOn : ""}`}
          />
        );
      })}
      {preset === "custom" && (
        // Native date input on purpose: it speaks YYYY-MM-DD directly, and the
        // value is read before setState because React 16 pools the event.
        <input
          type="date"
          data-testid="range-custom-date"
          value={customDate || ""}
          onChange={(e) => {
            const v = e.target.value;
            onCustomDate(v);
          }}
          className={classes.dateInput}
        />
      )}
    </Box>
  );
}

export default function FilterBar({ search, onSearch, band, onBand, preset, onPreset, customDate, onCustomDate }) {
  const classes = useStyles();
  return (
    <Paper variant="outlined" className={classes.bar}>
      <Typography component="div" className={classes.heading}>
        <FilterListIcon style={{ fontSize: 16, color: tokens.primary }} />
        Filters
      </Typography>
      <Box display="flex" alignItems="flex-start" flexWrap="wrap" gridGap={12}>
        <Box style={{ flex: "1.4 1 220px", minWidth: 200 }}>
          <span className={classes.fieldLabel}>Search</span>
          <TextField
            fullWidth
            className={classes.field}
            placeholder="Bot name or project id"
            value={search}
            onChange={(e) => {
              const v = e.target.value;
              onSearch(v);
            }}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 15, color: tokens.textHint }} />
                </InputAdornment>
              ),
            }}
            inputProps={{ "data-testid": "fleet-search" }}
          />
        </Box>
        <Box style={{ flex: "0.9 1 150px", minWidth: 140 }}>
          <span className={classes.fieldLabel}>Health band</span>
          <TextField
            select
            fullWidth
            className={classes.field}
            value={band}
            onChange={(e) => {
              const v = e.target.value;
              onBand(v);
            }}
            InputProps={{ disableUnderline: true }}
            SelectProps={{ native: true }}
            inputProps={{ "data-testid": "band-select" }}
          >
            {BAND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </TextField>
        </Box>
        <Box style={{ flex: "0 0 auto" }}>
          <span className={classes.fieldLabel}>Date range</span>
          <RangeChips preset={preset} onPreset={onPreset} customDate={customDate} onCustomDate={onCustomDate} />
        </Box>
      </Box>
    </Paper>
  );
}
